// Networking helpers: hard timeouts and mirror racing.

/**
 * Returns an AbortSignal that fires after `ms` or when `outer` aborts.
 */
export function withTimeout(ms, outer) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(new DOMException('Timeout', 'TimeoutError')), ms);
  if (outer) {
    if (outer.aborted) {
      clearTimeout(timer);
      ctrl.abort(outer.reason);
    } else {
      outer.addEventListener(
        'abort',
        () => { clearTimeout(timer); ctrl.abort(outer.reason); },
        { once: true }
      );
    }
  }
  return ctrl.signal;
}

/**
 * Promise.any with a clean error when everything fails.
 */
export async function raceFirst(promises, failMessage = 'All servers failed') {
  try {
    return await Promise.any(promises);
  } catch {
    throw new Error(failMessage);
  }
}

/**
 * Runs `primary()`. If it hasn't resolved after `delayMs`, ALSO starts
 * `hedge()` — resolves with whichever succeeds first, and only rejects if
 * both fail. Bounds worst-case latency to roughly delayMs + the hedge's own
 * response time, without giving up on `primary` (usually the higher-quality
 * source) the moment it's healthy but just needs a bit more time than usual.
 * Resolves to { from: 'primary' | 'hedge', value }.
 */
export function hedgedRace(primary, hedge, delayMs, onHedge) {
  return new Promise((resolve, reject) => {
    let settled = false;
    let hedgeStarted = false;
    let failures = 0;
    let hedgeTimer = null;

    const succeed = (from, value) => {
      if (settled) return;
      settled = true;
      clearTimeout(hedgeTimer);
      resolve({ from, value });
    };
    const fail = (from, err) => {
      if (settled) return;
      // An abort means the whole request was cancelled (e.g. superseded by a
      // newer edit) — propagate immediately rather than starting the hedge,
      // which would just fire a wasted request for a cancelled edit.
      if (err?.name === 'AbortError') {
        settled = true;
        clearTimeout(hedgeTimer);
        reject(err);
        return;
      }
      failures++;
      if (failures >= 2) {
        settled = true;
        clearTimeout(hedgeTimer);
        reject(err);
        return;
      }
      if (from === 'primary' && !hedgeStarted) startHedge();
    };
    const startHedge = () => {
      if (hedgeStarted || settled) return;
      hedgeStarted = true;
      clearTimeout(hedgeTimer);
      onHedge?.();
      hedge().then(v => succeed('hedge', v), e => fail('hedge', e));
    };

    hedgeTimer = setTimeout(startHedge, delayMs);
    primary().then(v => succeed('primary', v), e => fail('primary', e));
  });
}
